import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "../components/Reveal";
import { MENU, tr, trName } from "../data/menu";
import { useLang } from "../i18n";

const Row = ({ n, p, d, lang }) => (
  <li className="group break-inside-avoid border-b border-[#F5EDE3]/10 py-3.5">
    <div className="flex items-baseline gap-3">
      <span className="font-sans text-sm text-[#F5EDE3]/90 transition-colors duration-300 group-hover:text-[#D9B98A] md:text-base">
        {trName(n, lang)}
      </span>
      <span className="mb-1 flex-1 border-b border-dotted border-[#F5EDE3]/25" />
      <span className="font-sans text-sm tabular-nums text-[#D9B98A] md:text-base">{p} €</span>
    </div>
    {d && <p className="mt-1 max-w-[40ch] font-sans text-xs text-[#F5EDE3]/40">{tr(d, lang)}</p>}
  </li>
);

const Photos = ({ photos }) => (
  <div className="-mx-6 mb-12 flex gap-2 overflow-x-auto px-6 pb-2 [scrollbar-width:none] md:mx-0 md:mb-16 md:grid md:grid-cols-5 md:gap-3 md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden">
    {photos.map((ph) => (
      <div key={ph.src} className="group relative aspect-[4/5] w-[46%] shrink-0 overflow-hidden bg-[#F5EDE3]/5 md:w-auto">
        <img
          src={ph.src}
          alt={ph.alt}
          loading="lazy"
          className="h-full w-full object-cover opacity-90 transition-all duration-[2000ms] ease-out group-hover:scale-[1.06] group-hover:opacity-100"
        />
      </div>
    ))}
  </div>
);

const Body = ({ cat, lang }) => (
  <div>
    {cat.photos && <Photos photos={cat.photos} />}
    {cat.note && (
      <p className="v1-menu-note mb-8 font-sans text-[10px] uppercase tracking-[0.3em] text-[#C4886B]">{tr(cat.note, lang)}</p>
    )}

    {cat.items && (
      <ul className="md:columns-2 md:gap-16">
        {cat.items.map((it) => (
          <Row key={it.n} lang={lang} {...it} />
        ))}
      </ul>
    )}

    {cat.groups && (
      <div className="grid gap-12 md:grid-cols-3 md:gap-10">
        {cat.groups.map((g) => (
          <div key={g.key}>
            <h4 className="v1-menu-group-title mb-5 font-sans text-[10px] uppercase tracking-[0.28em] text-[#D9B98A]">{tr(g.title, lang)}</h4>
            {g.items && (
              <ul className="border-t border-[#F5EDE3]/10">
                {g.items.map((it) => (
                  <Row key={it.n} lang={lang} {...it} />
                ))}
              </ul>
            )}
            {g.flat && (
              <ul className="space-y-2.5">
                {tr(g.flat, lang).map((f) => (
                  <li key={f} className="font-sans text-sm text-[#F5EDE3]/75">
                    {f}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    )}

    {cat.steps && (
      <div className="grid gap-10 md:grid-cols-3">
        {cat.steps.map((s) => (
          <div key={s.k} className="border-t border-[#D9B98A]/30 pt-6">
            <span className="font-serif text-4xl text-[#D9B98A]/50">{s.k}</span>
            <h4 className="v1-menu-group-title mt-4 font-sans text-[10px] uppercase tracking-[0.28em] text-[#D9B98A]">{tr(s.t, lang)}</h4>
            <p className="mt-3 font-sans text-sm leading-relaxed text-[#F5EDE3]/70">{tr(s.v, lang)}</p>
          </div>
        ))}
      </div>
    )}

    {cat.foot && <p className="mt-12 font-sans text-xs text-[#F5EDE3]/45">{tr(cat.foot, lang)}</p>}
  </div>
);

export const Carta = () => {
  const [active, setActive] = useState(MENU[0].id);
  const { t, lang } = useLang();
  const cat = MENU.find((c) => c.id === active);

  return (
    <section id="carta" data-testid="carta-section" className="relative bg-[#2B1E16] px-6 py-24 md:px-12 md:py-40">
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px]">
        <Reveal>
          <p className="label text-[#D9B98A]">{t.carta.label}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 max-w-[20ch] font-serif text-4xl font-light leading-[1.06] text-[#F5EDE3] sm:text-5xl lg:text-6xl">
            {t.carta.title}
          </h2>
        </Reveal>

        <div className="v1-menu-tabs mt-14 flex gap-2 overflow-x-auto pb-4 md:mt-20 md:flex-wrap md:gap-x-8 md:gap-y-4 md:overflow-visible md:pb-0" role="tablist" aria-label={t.carta.label}>
          {MENU.map((c) => (
            <button
              key={c.id}
              data-testid={`carta-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              role="tab"
              aria-selected={active === c.id}
              aria-controls="carta-panel"
              className={`v1-menu-tab group relative shrink-0 pb-2 font-sans text-[10px] uppercase tracking-[0.24em] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9B98A] md:text-[11px] ${
                active === c.id ? "text-[#D9B98A]" : "text-[#F5EDE3]/45 hover:text-[#F5EDE3]"
              }`}
            >
              {tr(c.label, lang)}
              <span
                className={`absolute bottom-0 start-0 h-[1px] bg-[#D9B98A] transition-all duration-500 ease-out ${
                  active === c.id ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </button>
          ))}
        </div>

        <div className="mt-10 h-[1px] w-full bg-[#D9B98A]/20" />

        <AnimatePresence mode="wait">
          <motion.div
            key={`${active}-${lang}`}
            id="carta-panel"
            data-testid={`carta-panel-${active}`}
            role="tabpanel"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="pt-12 md:pt-16"
          >
            <Body cat={cat} lang={lang} />
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
